import React, { ChangeEvent, useState } from 'react';
import { FormControl } from '@uhg-harmony/uitk-react';
import styled from 'styled-components';
import { getCombinedFontsForTheme } from 'builder/util/getCombinedFonts';
import {
  StyledClose,
  StyledPickerHeader,
  StyledPickerPopover,
  StyledPickerWrapper,
} from 'builder/components/GradientColorPicker/GradientColorPicker';
import { ColorButton } from 'builder/components/ColorButton/ColorButton';
import { MainLabelStyles } from 'builder/components/WidgetConfigurations/Typography/Typography';
import { ColorButtonWrapper } from 'builder/components/WidgetConfigurations/Background/Background';
import GradientColorPicker from 'builder/components/GradientColorPicker/GradientColorPicker';
import { FontSelect } from 'builder/components/WidgetConfigurations/Accordion/AccordionTypography';

const FontDropdowns = styled.div`
  display: flex;
  padding-left: 50px;
  margin-top: -25px;
`;

const StyledWrapper = styled.div`
  padding-top: 0.5rem;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const TextIconWrapper = styled.div`
  cursor: pointer;
  font-family: OptumSans, sans-serif;
  text-align: center;
  padding-right: 5px;
  border-radius: 7px;
  &:hover {
    background: #d9f6fa;
  }
`;

export const StyledCustomIcon = styled.span`
  height: 1em;
`;

export interface SearchTypographyProps<T> {
  handleFont: (data: string) => void;
  handleTextColor: (data: string) => void;
  widgetState: Widget<T>;
  pages: Array<Page>;
  site?: Site;
  updateSite?: (site: Site) => void;
  saveSite?: () => void;
  color: string;
}

export const SearchTypography: React.FC<SearchTypographyProps<any>> = (
  searchTypographyProps: SearchTypographyProps<any>
) => {
  const {
    handleFont,
    handleTextColor,
    widgetState,
    site,
    updateSite,
    saveSite,
  } = searchTypographyProps;

  const [pickerAnchorEl, setPickerAnchorEl] = useState<HTMLDivElement | null>(
    null
  );
  const pickerOpen = Boolean(pickerAnchorEl);
  const pickerId = pickerOpen ? 'simple-popover-search-typography' : undefined;

  const handlePickerClose = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
    setPickerAnchorEl(null);
  };

  const handleFontChange = (e: ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    handleFont(e.target.value);
  };

  const handleColorEditClick = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
    e.preventDefault();
    setPickerAnchorEl(e.currentTarget);
  };
  const handlePickerClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <>
      <MainLabelStyles>Font</MainLabelStyles>
      <FontDropdowns>
        <FormControl
          data-test-id="font-select-dropdown"
          id={'controlled-select'}
        >
          <FontSelect
            onChange={handleFontChange}
            value={widgetState.config.font}
          >
            {Object.keys(getCombinedFontsForTheme()).map(f => {
              return (
                <option value={f} key={f}>
                  {f}
                </option>
              );
            })}
          </FontSelect>
        </FormControl>
      </FontDropdowns>
      <StyledWrapper>
        <MainLabelStyles>Text Color</MainLabelStyles>
        <ColorButtonWrapper
          id="text-color"
          data-test-id="text-color-icon"
          onClick={handleColorEditClick}
        >
          <ColorButton color={widgetState.config.textColor} />
        </ColorButtonWrapper>
        <StyledPickerPopover
          data-test-id="widget-color-picker-popover"
          id={pickerId}
          open={pickerOpen}
          onClick={handlePickerClick}
          onClose={handlePickerClose}
          anchorEl={pickerAnchorEl}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
        >
          <StyledPickerWrapper>
            <StyledPickerHeader data-test-id="styled-picker-header">
              Regular Text Color Selector
            </StyledPickerHeader>
            <StyledClose
              data-test-id="close-icon"
              onClick={handlePickerClose}
            />
          </StyledPickerWrapper>
          <GradientColorPicker
            value={widgetState.config.textColor}
            site={site}
            handleColorChange={handleTextColor}
            onChange={handleTextColor}
            updateSite={updateSite}
            saveSite={saveSite}
            anchorEl={pickerAnchorEl}
          ></GradientColorPicker>
        </StyledPickerPopover>
      </StyledWrapper>
    </>
  );
};
